import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "../../lib/supabase";
import {
  effectivePaymentStatus,
  paymentStatusInput,
} from "@/lib/paymentStatus";
import { isTestAccount } from "@/lib/is-test-account";
import {
  demoOwnedPropertyIds as demoOwnedPropertyIds_,
  withoutDemoProperties,
  type OwnerDemoFlag,
  type PropertyOwnerLink,
} from "@/lib/analytics-demo-inventory";
import { useTestAccountFilters } from "@/hooks/v2/useTestAccountFilters";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { useOperator } from "@/hooks/useOperator";
import { useOperatorBranding } from "@/hooks/useOperatorBranding";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Calendar,
  Download,
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ArrowUpRight,
  ArrowDownRight,
} from "lucide-react";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  downloadAnalyticsExcel,
  downloadAnalyticsPdf,
  type ActiveTenantExportRow,
  type AnalyticsMetricsSnapshot,
} from "@/lib/analyticsExport";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  CartesianGrid,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { getFullMonthlyRentFromBooking } from "@/lib/rent";
import { isBookingContractSignedIncludingManual } from "@/lib/leaseStatus";
import { managementDisplayStatus } from "@/hooks/v2/useManagementInvoices";
import { isOffsetInvoice } from "@/types/billing";
import { initialsFromName } from "@/lib/v2-format";
import { useLessorIdByOwnerUser } from "@/hooks/v2/owner-user-lessor";
import { usePlatformLessor } from "@/hooks/v2/useProperties";
import {
  resolveRentUnits,
  collectedRentEUR,
  splitRentRevenue,
  offsetInvoicedUnitMonths,
  analyticsDate,
  type RentPaymentInput,
  type RentRevenueSplit,
  type RentUnitApartment,
  type RentUnitProperty,
} from "@/lib/analytics-revenue";

type TimeRange = "today" | "week" | "month" | "year" | "custom" | "all";

/** How often the invoice queries refetch while Analytics is open (COL-630).
 *  management_invoices / saas_invoices are not in the realtime publication, so
 *  a payout confirmed by another admin reaches this page by polling. */
const ANALYTICS_INVOICE_POLL_MS = 60_000;

const PROPERTY_PERFORMANCE_PAGE_SIZE = 4;

/**
 * Date used to assign a payment to a calendar period in analytics.
 * Rent: billing period (`due_date`) so manual rent recorded late still counts in the month owed.
 * Deposits, etc.: when the row was recorded (`created_at`), with `due_date` fallback.
 */
function getPaymentBucketDateForAnalytics(p: {
  type?: string | null;
  due_date?: string | null;
  created_at?: string | null;
}): Date | null {
  const kind = (p.type || "").toLowerCase();
  if (kind === "rent") {
    if (p.due_date) {
      const d = String(p.due_date).slice(0, 10);
      return new Date(`${d}T12:00:00`);
    }
    if (p.created_at) return new Date(p.created_at);
    return null;
  }
  if (p.created_at) return new Date(p.created_at);
  if (p.due_date) {
    const d = String(p.due_date).slice(0, 10);
    return new Date(`${d}T12:00:00`);
  }
  return null;
}

/** Whether a booking is currently in effect (today falls within its term). */
function bookingIsCurrent(
  b: { start_date?: string | null; end_date?: string | null },
  now: Date
): boolean {
  const start = b.start_date ? new Date(b.start_date) : null;
  const end = b.end_date ? new Date(b.end_date) : null;
  return !!(start && end && start <= now && end >= now);
}

/**
 * Whether a booking's rent AND deposit are settled — via real paid `payments`
 * rows, OR the booking-level admin override `manually_marked_paid` (used when a
 * tenant pays outside Stripe and an admin uploads/records it). Mirrors the
 * tenant pipeline's `rentPaid || manuallyMarkedPaid` rule.
 */
function bookingRentAndDepositPaid(
  b: { manually_marked_paid?: boolean | null },
  uid: string,
  paidRentUsers: Set<string>,
  paidDepositUsers: Set<string>
): boolean {
  if (b.manually_marked_paid === true) return true;
  return paidRentUsers.has(uid) && paidDepositUsers.has(uid);
}

/**
 * Single source of truth for "active tenant". A tenant is active when their
 * booking is current, not manually deactivated, the contract is SIGNED (DocuSign
 * OR manual upload), and both rent and deposit are settled (real payments OR the
 * admin manual-paid override). Every active-tenant / occupancy count on this
 * dashboard derives from this predicate so the numbers always agree.
 */
function isActiveTenantBooking(
  b: {
    user_id?: string | number | null;
    tenant_active?: boolean | null;
    contract_status?: string | null;
    contract_signed_at?: string | null;
    manual_contract_upload?: boolean | null;
    signed_contract_url?: string | null;
    manually_marked_paid?: boolean | null;
    start_date?: string | null;
    end_date?: string | null;
  },
  now: Date,
  paidRentUsers: Set<string>,
  paidDepositUsers: Set<string>
): boolean {
  const uid = b.user_id != null ? String(b.user_id) : "";
  if (!uid) return false;
  if (b.tenant_active === false) return false;
  if (!bookingIsCurrent(b, now)) return false;
  if (!isBookingContractSignedIncludingManual(b)) return false;
  return bookingRentAndDepositPaid(b, uid, paidRentUsers, paidDepositUsers);
}

/** Build the paid-rent / paid-deposit user sets used by the active predicate. */
function buildPaidUserSets(payments: any[] | undefined): {
  paidRentUsers: Set<string>;
  paidDepositUsers: Set<string>;
} {
  const paidRentUsers = new Set<string>();
  const paidDepositUsers = new Set<string>();
  (payments || []).forEach((p: any) => {
    // COL-227: prefer the canonical effective status (Stripe rows trust
    // payments.status; manual rows need an approved proof) when present.
    if ((p?.effective_status ?? p?.status) !== "paid") return;
    const uid = p.user_id != null ? String(p.user_id) : "";
    if (!uid) return;
    if (p.type === "rent") paidRentUsers.add(uid);
    if (p.type === "deposit") paidDepositUsers.add(uid);
  });
  return { paidRentUsers, paidDepositUsers };
}

/** Thin progress bar used inside the KPI cards to show a collection/occupancy %. */
function MiniBar({
  pct,
  className = "bg-primary",
}: {
  pct: number;
  className?: string;
}) {
  return (
    <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-muted">
      <div
        className={`h-full rounded-full transition-all ${className}`}
        style={{ width: `${Math.max(0, Math.min(100, pct))}%` }}
      />
    </div>
  );
}

/**
 * Six-month sparkline under a revenue stream card: one bar per calendar month,
 * the current month last and fully opaque. All-zero series render a hairline
 * per month so an empty stream still reads as "six quiet months".
 */
function Sparkline({
  values,
  className,
  label,
}: {
  values: number[];
  className: string;
  label: string;
}) {
  const max = Math.max(0, ...values);
  return (
    <div
      className="mt-3 flex h-9 items-end gap-1"
      role="img"
      aria-label={label}
    >
      {values.map((v, i) => {
        const pct = max > 0 ? (v / max) * 100 : 0;
        const last = i === values.length - 1;
        return (
          <span
            key={i}
            className={`block flex-1 rounded-sm transition-all ${className} ${
              last ? "opacity-100" : "opacity-40"
            }`}
            style={{ height: `${Math.max(pct, 4)}%` }}
          />
        );
      })}
    </div>
  );
}

export const AdminAnalytics = () => {
  const { t, language } = useLanguage();
  // Operators must see their own name where these labels say "CoLiviq".
  const { brandText } = useOperatorBranding();
  // rooms/properties are public-read, so scope the inventory queries to an
  // operator's own org here (staff keep the global view).
  const { isOperatorPortal } = useAuth();
  const { operator } = useOperator();
  const operatorId = isOperatorPortal ? (operator?.id ?? null) : null;
  const navigate = useNavigate();
  const [timeRange, setTimeRange] = useState<TimeRange>("month");
  const [selectedProperty, setSelectedProperty] = useState<string>("all");
  const [propertyPerformancePage, setPropertyPerformancePage] = useState(1);
  const [customFrom, setCustomFrom] = useState<string>("");
  const [customTo, setCustomTo] = useState<string>("");
  const [cityFilter, setCityFilter] = useState<string>("all");
  const [roomSearch, setRoomSearch] = useState<string>("");
  // Test/CoLiviq accounts skew occupancy/tenant/revenue numbers the same way
  // they skew Bookings & Tenants — hidden by default here too, for the same
  // reason and the same predicate (src/lib/is-test-account.ts).
  const [includeTestAccounts, setIncludeTestAccounts] = useState(false);
  const { data: testAccountFilters } = useTestAccountFilters();
  const dynamicTestEmails = useMemo(
    () => (testAccountFilters ?? []).map((f) => f.email),
    [testAccountFilters]
  );

  // Fetch all bookings with payment data
  const { data: bookings } = useQuery({
    queryKey: ["admin-analytics-bookings"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("bookings")
        .select(
          `
          *,
          rooms (
            id,
            title,
            price,
            properties (
              id,
              title,
              city
            )
          )
        `
        )
        .in("status", ["confirmed", "pending"]);

      if (error) throw error;
      return data || [];
    },
  });

  // Every booking regardless of status (cancelled/terminated included),
  // used only to resolve a payment's property in propertyScopedPayments —
  // occupancy/tenant metrics must keep reading the status-filtered
  // `bookings` above, not this.
  const { data: bookingsForPaymentAttribution } = useQuery({
    queryKey: ["admin-analytics-bookings-for-payment-attribution"],
    queryFn: async () => {
      const { data, error } = await supabase.from("bookings").select(
        `
          id,
          user_id,
          room_id,
          start_date,
          status,
          rooms (
            id,
            properties (
              id
            )
          )
        `
      );

      if (error) throw error;
      return data || [];
    },
  });

  // Fetch all rooms (for accurate room counts per property)
  type AnalyticsRoom = {
    id: number;
    property_id: number | null;
    /** rooms.apartment_id — V2 column. */
    apartment_id: number | null;
    properties: { id: number; title: string; city: string } | null;
  };
  const { data: rooms } = useQuery({
    queryKey: ["admin-analytics-rooms", operatorId],
    enabled: !isOperatorPortal || operatorId != null,
    queryFn: async (): Promise<AnalyticsRoom[]> => {
      let roomQuery = supabase.from("rooms").select(`
          id,
          property_id,
          apartment_id,
          operator_id,
          properties (
            id,
            title,
            city
          )
        `);
      if (operatorId) roomQuery = roomQuery.eq("operator_id", operatorId);
      const { data, error } = await roomQuery;

      if (error) throw error;
      return (data || []) as AnalyticsRoom[];
    },
  });

  // Ownership + fee config per unit, so paid rent can be split into CoLiviq's
  // own rent vs. rent collected for third-party owners (COL-422). Same
  // inheritance chain the invoice generator bills by — see
  // src/lib/analytics-revenue.ts.
  const {
    data: apartmentsForOwnership,
    isFetched: apartmentsForOwnershipFetched,
    isError: apartmentsForOwnershipErrored,
  } = useQuery({
    queryKey: ["admin-analytics-apartments-ownership"],
    queryFn: async (): Promise<RentUnitApartment[]> => {
      const { data, error } = await supabase
        .from("apartments")
        .select(
          "id, property_id, lessor_id, owner_user_id, collection_mode, revenue_model, coliviq_fee_pct, coliviq_fee_amount, master_rent"
        );
      if (error) throw error;
      return (data || []) as RentUnitApartment[];
    },
  });
  const {
    data: propertiesForOwnership,
    isFetched: propertiesForOwnershipFetched,
    isError: propertiesForOwnershipErrored,
  } = useQuery({
    queryKey: ["admin-analytics-properties-ownership"],
    queryFn: async (): Promise<RentUnitProperty[]> => {
      const { data, error } = await supabase
        .from("properties")
        .select(
          "id, lessor_id, collection_mode, revenue_model, coliviq_fee_pct, coliviq_fee_amount, master_rent"
        );
      if (error) throw error;
      return (data || []) as RentUnitProperty[];
    },
  });
  const { data: platformLessor, isFetched: platformLessorFetched } =
    usePlatformLessor();
  const { data: lessorByOwnerUser } = useLessorIdByOwnerUser();

  // Whether the gross/net split has everything it needs to classify rent
  // correctly. Until both ownership queries have settled, `resolveRentUnits`
  // sees empty apartment/property lists and resolves EVERY room to "no
  // lessor" → CoLiviq's own — indistinguishable from the legitimate "this
  // unit really has no lessor" case. A genuine query failure would make that
  // wrong total permanent, not just a loading flicker, so it's surfaced
  // explicitly rather than silently publishing a split computed from missing
  // configuration (COL-422 review finding).
  const ownershipDataError =
    apartmentsForOwnershipErrored || propertiesForOwnershipErrored;
  const ownershipDataReady =
    !ownershipDataError &&
    apartmentsForOwnershipFetched &&
    propertiesForOwnershipFetched;

  const rentUnitByRoom = useMemo(
    () =>
      resolveRentUnits(
        rooms || [],
        apartmentsForOwnership || [],
        propertiesForOwnership || [],
        lessorByOwnerUser || {},
        platformLessor?.id ?? null
      ),
    [
      rooms,
      apartmentsForOwnership,
      propertiesForOwnership,
      lessorByOwnerUser,
      platformLessor,
    ]
  );

  // Fetch all payments
  const { data: payments } = useQuery({
    queryKey: ["admin-analytics-payments"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("payments")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      const rows = (data || []) as any[];

      // Canonical effective status (#743 / COL-227): Stripe rows trust
      // payments.status; manual rows count as paid only with an APPROVED
      // proof. Without this, analytics revenue disagreed with PaymentsHub /
      // tenant Dashboard, which both demote unreviewed manual "paid" rows.
      const manualIds = rows
        .filter((p) => !(p.stripe_payment_intent_id || p.stripe_charge_id))
        .map((p) => p.id);
      const proofStatusByPayment = new Map<string, string | null>();
      for (let i = 0; i < manualIds.length; i += 200) {
        const chunk = manualIds.slice(i, i + 200);
        const { data: proofs } = await supabase
          .from("payment_proofs")
          .select("payment_id, status, created_at")
          .in("payment_id", chunk)
          .order("created_at", { ascending: false });
        for (const pr of proofs || []) {
          if (!proofStatusByPayment.has(pr.payment_id)) {
            proofStatusByPayment.set(pr.payment_id, pr.status ?? null);
          }
        }
      }

      return rows.map((p) => ({
        ...p,
        // Same canonical rule as every other surface (#1326): a manual row
        // counts as paid on an approved proof, or on `paid` backed by a proof
        // on file or an admin attestation. `payments` is selected with `*`, so
        // marked_paid_at / marked_paid_by are on the row.
        effective_status: effectivePaymentStatus(
          paymentStatusInput(
            p,
            proofStatusByPayment.has(p.id)
              ? { status: proofStatusByPayment.get(p.id) ?? null }
              : null
          )
        ),
      }));
    },
  });

  // COL-630: keep the money figures live while the page is open. `payments`
  // and `payment_proofs` are published to Supabase realtime, so a rent row
  // recorded or a proof approved anywhere refetches the payments query (one
  // refetch per burst — a bulk action fires an event per row). The invoice
  // tables are not published; their queries poll instead (see
  // ANALYTICS_INVOICE_POLL_MS), and this browser's own invoice actions already
  // invalidate them directly.
  const queryClient = useQueryClient();
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const refetchPayments = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: ["admin-analytics-payments"],
        });
      }, 1000);
    };
    const channel = supabase
      .channel("admin-analytics-live")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "payments" },
        refetchPayments
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "payment_proofs" },
        refetchPayments
      )
      .subscribe();
    return () => {
      clearTimeout(timer);
      supabase.removeChannel(channel);
    };
  }, [queryClient]);

  // Fetch tenant profiles for name display AND to resolve the email behind
  // any user_id this page shows — including a payment's user_id when that
  // user's booking isn't in the confirmed/pending set fetched above (e.g. a
  // cancelled booking with a lingering payment row), so the test-account
  // filter below can't miss them.
  const analyticsUserIds = useMemo(
    () => [
      ...new Set(
        [
          ...(bookings || []).map((b: any) => b.user_id),
          ...(payments || []).map((p: any) => p.user_id),
        ].filter(Boolean)
      ),
    ],
    [bookings, payments]
  );
  const { data: profiles } = useQuery({
    queryKey: ["admin-analytics-profiles", analyticsUserIds.join(",")],
    queryFn: async () => {
      if (analyticsUserIds.length === 0) return [];

      const { data, error } = await supabase
        .from("profiles")
        .select("id, name, email")
        .in("id", analyticsUserIds);

      if (error) throw error;
      return data || [];
    },
    enabled: analyticsUserIds.length > 0,
  });

  const profileByUserId = useMemo(
    () => new Map((profiles || []).map((p: any) => [p.id, p])),
    [profiles]
  );

  // Test/CoLiviq accounts excluded by default — bookings and payments below
  // read from these, not the raw queries, so occupancy/tenant/revenue numbers
  // agree with each other and with Bookings & Tenants. A booking's email is
  // either the guest email on the row itself or, for an account booking, the
  // linked profile's email; a payment only has the latter.
  const visibleBookings = useMemo(() => {
    if (!bookings) return [];
    if (includeTestAccounts) return bookings;
    return (bookings as any[]).filter((b: any) => {
      const email = b.email || profileByUserId.get(b.user_id)?.email;
      return !isTestAccount(email, dynamicTestEmails);
    });
  }, [bookings, profileByUserId, dynamicTestEmails, includeTestAccounts]);

  // Every payment (no date filter — revenueInRange below needs an arbitrary
  // window for the period-over-period comparison), test-account rows dropped.
  const testFilteredPayments = useMemo(() => {
    if (!payments) return [];
    if (includeTestAccounts) return payments;
    return (payments as any[]).filter((p: any) => {
      const email = profileByUserId.get(p.user_id)?.email;
      return !isTestAccount(email, dynamicTestEmails);
    });
  }, [payments, profileByUserId, dynamicTestEmails, includeTestAccounts]);

  // Properties whose analytics inventory is sales-demo data — in practice the
  // hidden demo property (COL-447). The rule itself lives in
  // `analytics-demo-inventory.ts`, where the cases that must NOT be excluded
  // are pinned by tests. It keys on `profiles.is_demo_account`, NOT on the
  // email-based `isTestAccount` rule the booking/payment filters use:
  // `property_owners` is a portal-ACCESS grant rather than unit ownership, so
  // a real building with nothing but a @coliviq.de login against it would
  // otherwise lose every room from the occupancy denominator while its real
  // tenants stayed in the numerator.
  const { data: demoOwnedPropertyIds } = useQuery({
    queryKey: ["admin-analytics-demo-owned-properties"],
    queryFn: async (): Promise<string[]> => {
      const { data: links, error } = await supabase
        .from("property_owners")
        .select("property_id, user_id");
      if (error) throw error;
      const rows = (links || []) as PropertyOwnerLink[];
      const ownerIds = [
        ...new Set(rows.map((r) => r.user_id).filter(Boolean)),
      ] as string[];
      if (ownerIds.length === 0) return [];

      const { data: owners, error: ownerError } = await supabase
        .from("profiles")
        .select("id, is_demo_account")
        .in("id", ownerIds);
      if (ownerError) throw ownerError;

      return demoOwnedPropertyIds_(
        rows,
        new Map(
          ((owners || []) as OwnerDemoFlag[]).map((owner) => [owner.id, owner])
        )
      );
    },
  });

  // The demo properties to leave out of the inventory metrics, or an empty set
  // when the "include test accounts" toggle is on. Both the room set and the
  // bookings counted against it read this ONE set through
  // `withoutDemoProperties`, so occupancy's denominator and numerator cannot
  // drift onto different predicates again (they did: rooms moved onto the
  // demo flag while bookings stayed on the email rule, and a demo property
  // housing a tenant on an ordinary address then reported negative
  // availability).
  const demoExcludedPropertyIds = useMemo(
    () =>
      new Set<string>(includeTestAccounts ? [] : demoOwnedPropertyIds || []),
    [demoOwnedPropertyIds, includeTestAccounts]
  );

  // Room-set counterpart of `visibleBookings` / `testFilteredPayments`, so
  // occupancy's numerator and denominator agree on which inventory counts.
  const visibleRooms = useMemo(
    () =>
      withoutDemoProperties(
        rooms || [],
        demoExcludedPropertyIds,
        (r: any) => r?.property_id
      ),
    [rooms, demoExcludedPropertyIds]
  );

  // Booking-side counterpart of `visibleRooms`. `visibleBookings` itself stays
  // unfiltered — `propertyOptions` and `activeTenantExportRows` read it — so
  // the exclusion lands here, on the set every metric below actually counts.
  const inventoryFilteredBookings = useMemo(
    () =>
      withoutDemoProperties(
        visibleBookings,
        demoExcludedPropertyIds,
        (b: any) => b?.rooms?.properties?.id
      ),
    [visibleBookings, demoExcludedPropertyIds]
  );

  // Property-scoped views of the data above — the "All properties" dropdown
  // is a page-wide scope selector, so every metric/breakdown below reads
  // from these instead of the raw visibleBookings/rooms/testFilteredPayments
  // (which stay unfiltered for propertyOptions and activeTenantExportRows).
  const propertyScopedRooms = useMemo(() => {
    if (selectedProperty === "all") return visibleRooms;
    return visibleRooms.filter(
      (r: any) => String(r?.properties?.id ?? "") === selectedProperty
    );
  }, [visibleRooms, selectedProperty]);

  const propertyScopedBookings = useMemo(() => {
    if (selectedProperty === "all") return inventoryFilteredBookings;
    return inventoryFilteredBookings.filter(
      (b: any) => String(b?.rooms?.properties?.id ?? "") === selectedProperty
    );
  }, [inventoryFilteredBookings, selectedProperty]);

  // Payments carry no property/room id directly, so resolve one via the
  // booking (or, failing that, the user's most recent booking) — same
  // resolution cityBreakdown/propertyBreakdown already use for city/property.
  // Resolved from bookingsForPaymentAttribution (every status), not
  // visibleBookings or the status-filtered `bookings` query, so a payment
  // tied to a since-cancelled/terminated booking still resolves a property
  // instead of being silently dropped from the filter.
  const propertyScopedPayments = useMemo(() => {
    if (selectedProperty === "all") return testFilteredPayments;
    const bookingIdToPropertyId = new Map<number, string>();
    const userIdToPropertyId = new Map<string, string>();
    const byRecency = [...(bookingsForPaymentAttribution || [])].sort(
      (a: any, b: any) => {
        const ta = a?.start_date ? new Date(a.start_date).getTime() : 0;
        const tb = b?.start_date ? new Date(b.start_date).getTime() : 0;
        return tb - ta;
      }
    );
    byRecency.forEach((b: any) => {
      const pid =
        b?.rooms?.properties?.id != null ? String(b.rooms.properties.id) : "";
      if (!pid) return;
      if (b.id != null && !bookingIdToPropertyId.has(Number(b.id))) {
        bookingIdToPropertyId.set(Number(b.id), pid);
      }
      if (b.user_id != null && !userIdToPropertyId.has(String(b.user_id))) {
        userIdToPropertyId.set(String(b.user_id), pid);
      }
    });
    return testFilteredPayments.filter((p: any) => {
      const pid =
        (p.booking_id != null
          ? bookingIdToPropertyId.get(Number(p.booking_id))
          : undefined) ??
        (p.user_id != null
          ? userIdToPropertyId.get(String(p.user_id))
          : undefined);
      return pid === selectedProperty;
    });
  }, [testFilteredPayments, bookingsForPaymentAttribution, selectedProperty]);

  // Payment → room, resolved the same way as the property above (its booking,
  // else the user's most recent booking), so paid rent can be attributed to a
  // billable unit and split by ownership.
  const paymentRoomId = useMemo(() => {
    const bookingIdToRoomId = new Map<number, string>();
    const userIdToRoomId = new Map<string, string>();
    const byRecency = [...(bookingsForPaymentAttribution || [])].sort(
      (a: any, b: any) => {
        const ta = a?.start_date ? new Date(a.start_date).getTime() : 0;
        const tb = b?.start_date ? new Date(b.start_date).getTime() : 0;
        return tb - ta;
      }
    );
    byRecency.forEach((b: any) => {
      const rid = b?.room_id != null ? String(b.room_id) : "";
      if (!rid) return;
      if (b.id != null && !bookingIdToRoomId.has(Number(b.id))) {
        bookingIdToRoomId.set(Number(b.id), rid);
      }
      if (b.user_id != null && !userIdToRoomId.has(String(b.user_id))) {
        userIdToRoomId.set(String(b.user_id), rid);
      }
    });
    return (p: any): string | null =>
      (p.booking_id != null
        ? bookingIdToRoomId.get(Number(p.booking_id))
        : undefined) ??
      (p.user_id != null ? userIdToRoomId.get(String(p.user_id)) : undefined) ??
      null;
  }, [bookingsForPaymentAttribution]);

  // Every PAID rent row in scope (no date filter), with the period it belongs
  // to and the room it was paid for — the input for the gross/net split over
  // any window (current period, previous period, sparkline months).
  const paidRentRows = useMemo(
    () =>
      propertyScopedPayments
        // COL-417: part-paid months belong here too — the cash has arrived and
        // the dashboard must move with it, not wait for full settlement.
        // collectedRentEUR decides how much of the row counts.
        .filter(
          (p: any) =>
            p.type === "rent" &&
            (p.effective_status === "paid" || collectedRentEUR(p) > 0)
        )
        .flatMap((p: any): (RentPaymentInput & { date: Date })[] => {
          const d = getPaymentBucketDateForAnalytics(p);
          if (!d || Number.isNaN(d.getTime())) return [];
          return [
            {
              amount: collectedRentEUR(p),
              date: d,
              roomId: paymentRoomId(p),
            },
          ];
        }),
    [propertyScopedPayments, paymentRoomId]
  );

  // Management-fee invoices (owner billing) — paid rows are "Management revenue".
  // Also carries id/due_date/lessor name so the dashboard can show invoiced totals,
  // outstanding fees, an overdue count, and the owner behind an outstanding fee.
  const { data: managementInvoices } = useQuery({
    queryKey: ["admin-analytics-management-invoices"],
    queryFn: async () => {
      const { data, error } = await supabase.from("management_invoices").select(
        `id, amount_cents, status, settlement, period_month, due_date, paid_at, created_at,
           property_id, apartment_id, fee_basis,
           lessors:lessor_id ( name )`
      );
      if (error) throw error;
      return data || [];
    },
    refetchInterval: ANALYTICS_INVOICE_POLL_MS,
  });

  // Unit-months an offset invoice already bills. Their fee is recognised when
  // that invoice is marked paid, so it must not ALSO be recognised off the
  // tenant's rent below — see offsetInvoicedUnitMonths.
  //
  // Read off the UNSCOPED invoice list on purpose: whether a unit is invoiced
  // is a fact about the unit, not about the property filter the admin happens
  // to have applied, and scoping it would let a filter change what the rent
  // split recognises.
  const offsetUnitMonths = useMemo(
    () => offsetInvoicedUnitMonths(managementInvoices || []),
    [managementInvoices]
  );

  const rentSplitInRange = useMemo(
    () =>
      (start: Date, end: Date): RentRevenueSplit =>
        // Pass the FULL row set, not pre-filtered to [start, end] — a unit's
        // monthly fee must be computed from its complete month, then
        // allocated into this window, never re-derived from a partial
        // month's rent (see splitRentRevenue's doc comment).
        splitRentRevenue(
          paidRentRows,
          rentUnitByRoom,
          { start, end },
          offsetUnitMonths
        ),
    [paidRentRows, rentUnitByRoom, offsetUnitMonths]
  );

  // A management invoice can be a legacy single-property row (property_id
  // set) or a lessor-consolidated row spanning several properties
  // (property_id NULL, per-property fee itemised in fee_basis.properties[] —
  // see 20260729140000_management_invoices_consolidate_per_lessor.sql).
  // Returns the cents attributable to `propertyId`, or null when the invoice
  // has no attribution to that property at all.
  const managementInvoiceCentsForProperty = (
    inv: any,
    propertyId: string
  ): number | null => {
    if (inv?.property_id != null) {
      return String(inv.property_id) === propertyId
        ? inv.amount_cents || 0
        : null;
    }
    const perProperty = inv?.fee_basis?.properties;
    if (Array.isArray(perProperty)) {
      const match = perProperty.find(
        (p: any) => String(p?.property_id ?? "") === propertyId
      );
      if (!match) return null;
      const feeEur = typeof match.fee_eur === "number" ? match.fee_eur : 0;
      return Math.round(feeEur * 100);
    }
    return null;
  };

  // Management invoices scoped to the selected property — each row's
  // amount_cents is replaced with just that property's slice, so every
  // downstream sum (revenue hero, outstanding-fees card, period trend)
  // reflects the filter instead of the lessor's full consolidated invoice.
  const propertyScopedManagementInvoices = useMemo(() => {
    if (selectedProperty === "all") return managementInvoices || [];
    return (managementInvoices || [])
      .map((inv: any) => {
        const cents = managementInvoiceCentsForProperty(inv, selectedProperty);
        if (cents == null) return null;
        return { ...inv, amount_cents: cents };
      })
      .filter(Boolean);
  }, [managementInvoices, selectedProperty]);

  // SaaS invoices — paid rows are "SaaS revenue".
  const { data: saasInvoices } = useQuery({
    queryKey: ["admin-analytics-saas-invoices"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("saas_invoices")
        .select("amount_cents, status, period_month, paid_at, created_at");
      if (error) throw error;
      return data || [];
    },
    refetchInterval: ANALYTICS_INVOICE_POLL_MS,
  });

  // SaaS subscriptions bill a subscriber, not a property — there is no
  // column or breakdown to attribute a row to one, so a property filter
  // can only ever show €0 here rather than the (misleading) portfolio-wide
  // total.
  const propertyScopedSaasInvoices = useMemo(() => {
    if (selectedProperty === "all") return saasInvoices || [];
    return [];
  }, [saasInvoices, selectedProperty]);

  // The windows below end at the end of TODAY (COL-630), so a page left open
  // past midnight must roll over to the new day rather than keep yesterday's.
  const [today, setToday] = useState(() => new Date().toDateString());
  useEffect(() => {
    const now = new Date();
    const nextMidnight = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate() + 1
    );
    const timer = setTimeout(
      () => setToday(new Date().toDateString()),
      nextMidnight.getTime() - now.getTime() + 1000
    );
    return () => clearTimeout(timer);
  }, [today]);

  // Calculate date range
  const dateRange = useMemo(() => {
    const now = new Date();
    let start: Date;

    const startOfDay = (d: Date) => {
      const x = new Date(d);
      x.setHours(0, 0, 0, 0);
      return x;
    };

    const endOfDay = (d: Date) => {
      const x = new Date(d);
      x.setHours(23, 59, 59, 999);
      return x;
    };

    // COL-630: every window runs to the END of today, not to this instant.
    // Date-only values (a rent due date, an invoice month) sit at noon of
    // their day, so a window ending at `now` hid anything dated today until
    // 12:00 — a payout confirmed in the morning did not reach Management
    // revenue until the afternoon.
    let end: Date = endOfDay(now);

    switch (timeRange) {
      case "today":
        start = startOfDay(now);
        break;
      case "week": {
        const d = startOfDay(now);
        d.setDate(d.getDate() - 6);
        start = d;
        break;
      }
      case "month":
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case "year":
        start = new Date(now.getFullYear(), 0, 1);
        break;
      case "custom": {
        const from = customFrom ? new Date(`${customFrom}T00:00:00`) : null;
        const to = customTo ? new Date(`${customTo}T00:00:00`) : null;
        start = from ? startOfDay(from) : startOfDay(now);
        end = to ? endOfDay(to) : endOfDay(now);
        break;
      }
      default:
        start = new Date(0);
    }

    return { start, end };
    // `today` is not read, it only re-runs the window when the day turns.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeRange, customFrom, customTo, today]);

  // Filter payments by date range (rent by due month; deposits etc. by
  // created_at). Test-account rows are already dropped in
  // testFilteredPayments, so every downstream revenue/collection number
  // agrees with the tenant/occupancy ones without re-deriving the email.
  const filteredPayments = useMemo(() => {
    return propertyScopedPayments.filter((p: any) => {
      const paymentDate = getPaymentBucketDateForAnalytics(p);
      if (!paymentDate || Number.isNaN(paymentDate.getTime())) return false;
      return paymentDate >= dateRange.start && paymentDate <= dateRange.end;
    });
  }, [propertyScopedPayments, dateRange]);

  // Financial metrics (split rent vs deposit)
  const financialMetrics = useMemo(() => {
    // COL-417: collected, not settled — a part-paid month contributes what
    // actually arrived, so the collection rate below reflects the bank balance
    // rather than a binary settled/unsettled count.
    const paidRent = filteredPayments
      .filter((p: any) => p.type === "rent")
      .reduce((sum: number, p: any) => sum + collectedRentEUR(p), 0);

    const expectedRent = filteredPayments
      .filter((p: any) => p.type === "rent")
      .reduce((sum: number, p: any) => sum + p.amount_cents / 100, 0);

    const rentCollectionRate =
      expectedRent > 0 ? (paidRent / expectedRent) * 100 : 0;

    const paidDeposit = filteredPayments
      .filter((p: any) => p.type === "deposit" && p.effective_status === "paid")
      .reduce((sum: number, p: any) => sum + p.amount_cents / 100, 0);

    const expectedDeposit = filteredPayments
      .filter((p: any) => p.type === "deposit")
      .reduce((sum: number, p: any) => sum + p.amount_cents / 100, 0);

    const depositCollectionRate =
      expectedDeposit > 0 ? (paidDeposit / expectedDeposit) * 100 : 0;

    const overdueRentAmount = filteredPayments
      .filter(
        (p: any) =>
          p.type === "rent" &&
          (p.effective_status === "overdue" ||
            (p.effective_status === "pending" &&
              p.due_date &&
              new Date(p.due_date) < new Date()))
      )
      .reduce((sum: number, p: any) => sum + p.amount_cents / 100, 0);

    return {
      paidRent,
      expectedRent,
      rentCollectionRate,
      paidDeposit,
      expectedDeposit,
      depositCollectionRate,
      overdueRentAmount,
    };
  }, [filteredPayments]);

  // Paid invoices (management / SaaS) received within a window. Recognised
  // when the money arrived (paid_at), not by billing month — otherwise
  // "today"/"week" (which never span a period_month = the 1st) read €0.
  const paidInvoicesInRange = useMemo(
    () => (rows: any[] | undefined, start: Date, end: Date) =>
      (rows || []).filter((r) => {
        if (r.status !== "paid") return false;
        // Offset-settled invoices ARE counted here now. They used to be
        // skipped because the rent split already recognised their fee the
        // moment the tenant paid; since 20260911120000 the pairing runs the
        // other way round — splitRentRevenue routes an invoiced unit-month
        // into `invoicedRetainedFees` (excluded from `retainedFees`), and this
        // paid invoice is what recognises it instead. Exactly one of the two
        // counts a given fee, and which one depends on whether a document
        // exists for it at all.
        const d = analyticsDate(r.paid_at || r.period_month || r.created_at);
        return d != null && d >= start && d <= end;
      }),
    []
  );

  // CoLiviq revenue streams for an arbitrary window (COL-422):
  //   rent       = rent on CoLiviq-owned units only (NET of pass-through rent)
  //   management = paid fee invoices + the fee CoLiviq keeps out of rent it
  //                collects for third-party owners WHERE NO INVOICE BILLS IT.
  //                A paid invoice is the source of truth wherever one exists;
  //                `retainedFees` covers only the units that issue none
  //                (a month the generator has not billed yet, or a unit it
  //                skips), so the two partition the fee instead of
  //                overlapping. See
  //                offsetInvoicedUnitMonths in src/lib/analytics-revenue.ts.
  //   saas       = paid SaaS invoices
  //   total      = CoLiviq's own earnings (net) — an offset-invoiced fee only
  //                joins this once its invoice is confirmed paid (ask 6)
  //   gross      = everything that flowed through CoLiviq's account, this
  //                window: ownRent + passThroughGross + saas. Deliberately
  //                NOT `total + passedOn` — `total` defers an offset fee
  //                until its invoice is paid, but `passedOn` (what's wired to
  //                the owner) withholds that same fee from the moment rent
  //                lands, not from invoice-paid time. Deriving gross from
  //                those two would make it lose the pending fee for the
  //                whole window between rent collection and invoice
  //                confirmation, even though the cash already sits in
  //                CoLiviq's account. Cash flow (gross/passedOn) and revenue
  //                recognition (total/management) are different questions;
  //                only the latter is meant to wait on the invoice.
  const streamsInRange = useMemo(
    () => (start: Date, end: Date) => {
      const sum = (rows: any[]) =>
        rows.reduce((s, r) => s + (r.amount_cents || 0) / 100, 0);
      const split = rentSplitInRange(start, end);
      const mgmtRows = paidInvoicesInRange(
        propertyScopedManagementInvoices as any[],
        start,
        end
      );
      const saasRows = paidInvoicesInRange(
        propertyScopedSaasInvoices as any[],
        start,
        end
      );
      const managementInvoiced = sum(mgmtRows);
      const management = managementInvoiced + split.retainedFees;
      const saas = sum(saasRows);
      const total = split.ownRent + management + saas;
      return {
        rent: split.ownRent,
        management,
        managementInvoiced,
        managementInvoiceCount: mgmtRows.length,
        saas,
        saasInvoiceCount: saasRows.length,
        total,
        gross: split.ownRent + split.passThroughGross + saas,
        split,
      };
    },
    [
      rentSplitInRange,
      paidInvoicesInRange,
      propertyScopedManagementInvoices,
      propertyScopedSaasInvoices,
    ]
  );

  // The selected period's streams, with each stream's share of the net total.
  const revenueStreams = useMemo(() => {
    const cur = streamsInRange(dateRange.start, dateRange.end);
    const pct = (v: number) => (cur.total > 0 ? (v / cur.total) * 100 : 0);
    return {
      ...cur,
      rentPct: pct(cur.rent),
      managementPct: pct(cur.management),
      saasPct: pct(cur.saas),
    };
  }, [streamsInRange, dateRange]);

  // Last six calendar months per stream, for the sparkline under each card
  // (the current month last). Property/test-account scope applies as above.
  const streamSparklines = useMemo(() => {
    const now = new Date();
    const months = Array.from({ length: 6 }, (_, i) => {
      const s = new Date(now.getFullYear(), now.getMonth() - (5 - i), 1);
      const e = new Date(s.getFullYear(), s.getMonth() + 1, 0, 23, 59, 59, 999);
      return streamsInRange(s, e);
    });
    return {
      rent: months.map((m) => m.rent),
      management: months.map((m) => m.management),
      saas: months.map((m) => m.saas),
    };
  }, [streamsInRange]);

  // Management-fee breakdown for the period: what was invoiced, what's still
  // outstanding, how many are overdue, and which owner an outstanding fee is for.
  // Invoiced/outstanding are bucketed by the invoice's billing month (period_month),
  // since an unpaid invoice has no paid_at to place it in a period.
  const managementMetrics = useMemo(() => {
    const inPeriod = (dateish: string | null | undefined) => {
      if (!dateish) return false;
      const d = new Date(`${String(dateish).slice(0, 10)}T12:00:00`);
      return (
        !Number.isNaN(d.getTime()) && d >= dateRange.start && d <= dateRange.end
      );
    };

    const rows = ((propertyScopedManagementInvoices as any[]) || []).filter(
      (r) => r.status !== "canceled" && inPeriod(r.period_month)
    );

    const money = (r: any) => (r.amount_cents || 0) / 100;
    const invoiced = rows.reduce((s, r) => s + money(r), 0);

    // "Outstanding" means money still owed to CoLiviq, so an OFFSET invoice is
    // excluded however unpaid it looks: its fee was deducted from the rent
    // CoLiviq collected, and nobody will ever transfer it. Counting it here
    // would inflate the headline outstanding total and — worse — list that
    // owner in the amber per-owner card as a debtor for an amount they do not
    // owe. `invoiced` above deliberately still includes it: the fee WAS billed.
    // Same rule as the Invoices tab's "Open now" tile.
    const outstandingRows = rows.filter(
      (r) => r.status !== "paid" && !isOffsetInvoice(r)
    );
    const outstanding = outstandingRows.reduce((s, r) => s + money(r), 0);
    const overdueRows = rows.filter(
      (r) => managementDisplayStatus(r) === "overdue"
    );
    const overdueCount = overdueRows.length;
    const overdueAmount = overdueRows.reduce((s, r) => s + money(r), 0);

    // Per-owner outstanding breakdown (fees are billed to the property owner).
    // Sorted by amount owed, so the card can list the biggest debtors first.
    type OwnerRow = {
      name: string;
      amount: number;
      count: number;
      overdue: number;
    };
    const byOwner = new Map<string, OwnerRow>();
    outstandingRows.forEach((r) => {
      const name = (r.lessors?.name || "").toString().trim() || "Unassigned";
      const row = byOwner.get(name) || {
        name,
        amount: 0,
        count: 0,
        overdue: 0,
      };
      row.amount += money(r);
      row.count += 1;
      if (managementDisplayStatus(r) === "overdue") row.overdue += 1;
      byOwner.set(name, row);
    });
    const owners = Array.from(byOwner.values()).sort(
      (a, b) => b.amount - a.amount
    );

    return {
      invoiced,
      outstanding,
      outstandingCount: outstandingRows.length,
      overdueCount,
      overdueAmount,
      owners,
      ownerCount: owners.length,
      topOwner: owners[0]?.name ?? null,
      // Invoiced fees only — the fee netted from pass-through rent was never
      // invoiced, so it must not count as "collected" against `invoiced`.
      collectedPct:
        invoiced > 0 ? (revenueStreams.managementInvoiced / invoiced) * 100 : 0,
    };
  }, [
    propertyScopedManagementInvoices,
    dateRange,
    revenueStreams.managementInvoiced,
  ]);

  // Active SaaS subscriptions → "N active subscription(s) · <plan>".
  // Distinct tenants with overdue rent in the period ("N tenants require attention").
  const overdueTenantsCount = useMemo(() => {
    const users = new Set<string>();
    (filteredPayments || []).forEach((p: any) => {
      if (p.type !== "rent") return;
      const isOverdue =
        p.status === "overdue" ||
        (p.status === "pending" &&
          p.due_date &&
          new Date(p.due_date) < new Date());
      if (!isOverdue) return;
      const uid = p.user_id != null ? String(p.user_id) : "";
      if (uid) users.add(uid);
    });
    return users.size;
  }, [filteredPayments]);

  // Net CoLiviq revenue for an arbitrary window — the period-over-period badge
  // compares like with like (own rent + management + SaaS, no pass-through).
  const revenueInRange = useMemo(
    () => (start: Date, end: Date) => streamsInRange(start, end).total,
    [streamsInRange]
  );

  // Period-over-period change for the hero (e.g. "+8.2% vs June"). Only the
  // fixed windows have a well-defined "previous period"; custom/all-time don't.
  const revenueComparison = useMemo(() => {
    const now = new Date();
    let prevStart: Date | null = null;
    let prevEnd: Date | null = null;
    let label = "";

    if (timeRange === "today") {
      const s = new Date(now);
      s.setDate(s.getDate() - 1);
      s.setHours(0, 0, 0, 0);
      const e = new Date(s);
      e.setHours(23, 59, 59, 999);
      prevStart = s;
      prevEnd = e;
      label = "yesterday";
    } else if (timeRange === "week") {
      const e = new Date(dateRange.start.getTime() - 1);
      const s = new Date(e);
      s.setDate(s.getDate() - 6);
      s.setHours(0, 0, 0, 0);
      prevStart = s;
      prevEnd = e;
      label = "last week";
    } else if (timeRange === "month") {
      const s = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const e = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
      prevStart = s;
      prevEnd = e;
      label = s.toLocaleDateString(language === "de" ? "de-DE" : "en-GB", {
        month: "long",
      });
    } else if (timeRange === "year") {
      const s = new Date(now.getFullYear() - 1, 0, 1);
      const e = new Date(now.getFullYear() - 1, 11, 31, 23, 59, 59, 999);
      prevStart = s;
      prevEnd = e;
      label = String(now.getFullYear() - 1);
    }

    // No defined "previous period" for custom ranges or all-time.
    if (!prevStart || !prevEnd) return null;

    const prevTotal = revenueInRange(prevStart, prevEnd);
    const current = revenueStreams.total;
    const delta = current - prevTotal;
    // % change is undefined when the previous period had no revenue.
    const pct = prevTotal > 0 ? (delta / prevTotal) * 100 : null;
    const direction: "up" | "down" | "flat" =
      delta > 0.005 ? "up" : delta < -0.005 ? "down" : "flat";

    return { prevTotal, current, delta, pct, direction, label };
  }, [
    timeRange,
    dateRange.start,
    revenueInRange,
    revenueStreams.total,
    language,
  ]);

  // Month/period name shown under "Deposit revenue" (e.g. "July").
  const depositPeriodLabel = useMemo(() => {
    const now = new Date();
    if (timeRange === "month")
      return now.toLocaleDateString(language === "de" ? "de-DE" : "en-GB", {
        month: "long",
      });
    if (timeRange === "year") return String(now.getFullYear());
    if (timeRange === "today")
      return t("admin.analytics.depositPeriodToday", "today");
    if (timeRange === "week")
      return t("admin.analytics.depositPeriodThisWeek", "this week");
    if (timeRange === "custom" && customFrom && customTo)
      return `${customFrom} → ${customTo}`;
    return t("admin.analytics.depositPeriodAllTime", "all time");
  }, [timeRange, customFrom, customTo, language, t]);

  // Current period name for the hero caption chip (e.g. "[July 2026]").
  const heroPeriod = useMemo(() => {
    const now = new Date();
    switch (timeRange) {
      case "today":
        return { name: t("admin.analytics.rangeTodayLabel", "Today") };
      case "week":
        return { name: t("admin.analytics.rangeThisWeekLabel", "This week") };
      case "month":
        return {
          name: now.toLocaleDateString(language === "de" ? "de-DE" : "en-GB", {
            month: "long",
            year: "numeric",
          }),
        };
      case "year":
        return { name: String(now.getFullYear()) };
      case "custom":
        return customFrom && customTo
          ? { name: `${customFrom} → ${customTo}` }
          : { name: t("admin.analytics.selectedPeriod", "Selected period") };
      default:
        return { name: t("admin.analytics.rangeAllTimeLabel", "All time") };
    }
  }, [timeRange, customFrom, customTo, t, language]);

  // Legend rows for the hero proportion bar (color · stream · share · amount).
  const revenueLegend = useMemo(
    () => [
      {
        key: "rent",
        label: "Rent",
        amount: revenueStreams.rent,
        pct: revenueStreams.rentPct,
        dot: "bg-emerald-400",
      },
      {
        key: "management",
        label: "Management",
        amount: revenueStreams.management,
        pct: revenueStreams.managementPct,
        dot: "bg-teal-800",
      },
      {
        key: "saas",
        label: "SaaS",
        amount: revenueStreams.saas,
        pct: revenueStreams.saasPct,
        dot: "bg-orange-300",
      },
    ],
    [revenueStreams]
  );

  // Calculate occupancy metrics
  const occupancyMetrics = useMemo(() => {
    const now = new Date();
    const totalRooms = (propertyScopedRooms || []).length;
    const occupiedRoomIds = new Set(
      (propertyScopedBookings || [])
        .filter((b: any) => {
          const startDate = b.start_date ? new Date(b.start_date) : null;
          const endDate = b.end_date ? new Date(b.end_date) : null;
          return startDate && endDate && startDate <= now && endDate >= now;
        })
        .map((b: any) => b.room_id)
        .filter(Boolean)
    );

    const occupiedRooms = occupiedRoomIds.size;

    // Active tenants — single source of truth (isActiveTenantBooking):
    // current booking + signed contract + paid rent + paid deposit.
    const { paidRentUsers, paidDepositUsers } = buildPaidUserSets(
      propertyScopedPayments as any[]
    );

    const qualifiedActiveUsers = new Set<string>();
    (propertyScopedBookings || []).forEach((b: any) => {
      if (isActiveTenantBooking(b, now, paidRentUsers, paidDepositUsers)) {
        qualifiedActiveUsers.add(String(b.user_id));
      }
    });

    const activeTenants = qualifiedActiveUsers.size;

    const occupancyRate =
      totalRooms > 0 ? (occupiedRooms / totalRooms) * 100 : 0;

    return {
      totalRooms,
      occupiedRooms,
      occupancyRate,
      availableRooms: totalRooms - occupiedRooms,
      activeTenants,
    };
  }, [propertyScopedBookings, propertyScopedRooms, propertyScopedPayments]);

  // City-wise breakdown (e.g. Tutzing, Augsburg)
  const cityBreakdown = useMemo(() => {
    const q = roomSearch.trim().toLowerCase();
    const matchesRoomQuery = (title: unknown) => {
      if (!q) return true;
      const t2 = (title || "").toString().toLowerCase();
      return t2.includes(q);
    };

    // Cities that have rooms at all, so a city with no bookings still shows.
    const citiesWithRooms = new Set<string>(
      (propertyScopedRooms || [])
        .filter((r: any) => matchesRoomQuery(r?.title))
        .map((r: any) => (r.properties?.city || "Unknown").toString())
    );

    const bookingIdToCity = new Map<number, string>();
    const userIdToCity = new Map<string, string>();

    (propertyScopedBookings || []).forEach((b: any) => {
      if (!matchesRoomQuery(b?.rooms?.title)) return;
      const city = (b.rooms?.properties?.city || "Unknown").toString();
      if (b.id != null) bookingIdToCity.set(Number(b.id), city);
      if (b.user_id != null && !userIdToCity.has(String(b.user_id)))
        userIdToCity.set(String(b.user_id), city);
    });

    const money = (p: any) => (p.amount_cents || 0) / 100;
    const perCity: Record<
      string,
      {
        city: string;
        paidRent: number;
        expectedRent: number;
        paidDeposit: number;
        expectedDeposit: number;
      }
    > = {};

    const ensure = (city: string) => {
      if (!perCity[city]) {
        perCity[city] = {
          city,
          paidRent: 0,
          expectedRent: 0,
          paidDeposit: 0,
          expectedDeposit: 0,
        };
      }
      return perCity[city];
    };

    // Seed from rooms so cities with 0 bookings still show
    citiesWithRooms.forEach((city) => ensure(city));
    // Seed from bookings too
    (propertyScopedBookings || [])
      .filter((b: any) => matchesRoomQuery(b?.rooms?.title))
      .forEach((b: any) =>
        ensure((b.rooms?.properties?.city || "Unknown").toString())
      );

    // Add payment-based expected/paid per city
    (filteredPayments || []).forEach((p: any) => {
      const city =
        (p.booking_id != null
          ? bookingIdToCity.get(Number(p.booking_id))
          : undefined) ||
        (p.user_id != null ? userIdToCity.get(String(p.user_id)) : undefined) ||
        "Unknown";

      const row = ensure(city);
      if (p.type === "rent") {
        row.expectedRent += money(p);
        // COL-417: same "collected, not settled" rule as financialMetrics.
        row.paidRent += collectedRentEUR(p);
      } else if (p.type === "deposit") {
        row.expectedDeposit += money(p);
        if (p.effective_status === "paid") row.paidDeposit += money(p);
      }
    });

    return Object.values(perCity).sort((a, b) => a.city.localeCompare(b.city));
  }, [
    propertyScopedBookings,
    propertyScopedRooms,
    filteredPayments,
    roomSearch,
  ]);

  const cityOptions = useMemo(() => {
    const set = new Set<string>();
    (propertyScopedRooms || []).forEach((r: any) => {
      const city = (r?.properties?.city || "").toString().trim();
      if (city) set.add(city);
    });
    (propertyScopedBookings || []).forEach((b: any) => {
      const city = (b?.rooms?.properties?.city || "").toString().trim();
      if (city) set.add(city);
    });
    return Array.from(set.values()).sort((a, b) => a.localeCompare(b));
  }, [propertyScopedRooms, propertyScopedBookings]);

  // The property filter can make the current city selection unavailable
  // (its city no longer among cityOptions) — reset to "all" rather than
  // silently filtering the city breakdown against a city that isn't there.
  useEffect(() => {
    setCityFilter((c) => (c === "all" || cityOptions.includes(c) ? c : "all"));
  }, [cityOptions]);

  const visibleCityBreakdown = useMemo(() => {
    return cityFilter === "all"
      ? cityBreakdown
      : cityBreakdown.filter((c) => c.city === cityFilter);
  }, [cityBreakdown, cityFilter]);

  // Property-wise breakdown
  const propertyBreakdown = useMemo(() => {
    const breakdown: Record<
      string,
      {
        property: string;
        tenants: number; // active tenants
        tenantNames: string[];
        revenue: number;
        expected: number;
        totalRooms: number;
        occupiedRooms: number;
        occupancyRate: number;
        id: string;
      }
    > = {};

    const profileNameById = (profiles || []).reduce<Record<string, string>>(
      (acc, p: any) => {
        if (!p?.id) return acc;
        acc[String(p.id)] = (p.name || p.email || "Tenant").toString();
        return acc;
      },
      {}
    );

    const roomsByPropertyId = (propertyScopedRooms || []).reduce<
      Record<string, { title: string; count: number }>
    >((acc, r: any) => {
      const prop = r.properties;
      if (!prop?.id) return acc;
      const key = String(prop.id);
      if (!acc[key])
        acc[key] = { title: prop.title || "Unknown Property", count: 0 };
      acc[key].count += 1;
      return acc;
    }, {});

    // Seed properties from rooms (so properties with no bookings still show occupancy)
    Object.entries(roomsByPropertyId).forEach(([propertyId, meta]) => {
      breakdown[propertyId] = {
        id: propertyId,
        property: meta.title,
        tenants: 0,
        tenantNames: [],
        revenue: 0,
        expected: 0,
        totalRooms: meta.count,
        occupiedRooms: 0,
        occupancyRate: 0,
      };
    });

    const now = new Date();
    const occupiedRoomIdsByProperty = new Map<string, Set<string | number>>();
    const { paidRentUsers, paidDepositUsers } = buildPaidUserSets(
      propertyScopedPayments as any[]
    );

    (propertyScopedBookings || []).forEach((booking: any) => {
      const property = booking.rooms?.properties;
      if (!property?.id) return;

      const propertyId = String(property.id);
      if (!breakdown[propertyId]) {
        breakdown[propertyId] = {
          id: propertyId,
          property: property.title || "Unknown Property",
          tenants: 0,
          tenantNames: [],
          revenue: 0,
          expected: 0,
          totalRooms: 0,
          occupiedRooms: 0,
          occupancyRate: 0,
        };
      }

      const isCurrent = bookingIsCurrent(booking, now);

      if (isCurrent) {
        const set =
          occupiedRoomIdsByProperty.get(propertyId) ||
          new Set<string | number>();
        if (booking.room_id != null) set.add(booking.room_id);
        occupiedRoomIdsByProperty.set(propertyId, set);

        const uid = booking.user_id != null ? String(booking.user_id) : "";
        const name = uid ? profileNameById[uid] : null;
        // Count tenant only if active by the shared rule (signed + rent + deposit).
        // COL-227: the paid sets are built from effective_status (ledger truth).
        if (
          isActiveTenantBooking(booking, now, paidRentUsers, paidDepositUsers)
        ) {
          breakdown[propertyId].tenants += 1;
          if (name) breakdown[propertyId].tenantNames.push(name);
        }
      }

      const rent = getFullMonthlyRentFromBooking(booking);
      breakdown[propertyId].expected += Number(rent);

      // Calculate revenue for this property. Deposits are a liability, not
      // revenue, and `expected` above is rent-only — so exclude deposit
      // payments here to keep property revenue (and its collection rate)
      // consistent with the separated rent/deposit KPIs (COL-155).
      const propertyPayments = filteredPayments.filter((p: any) => {
        return (
          (p.booking_id === booking.id || p.user_id === booking.user_id) &&
          p.effective_status === "paid" &&
          p.type !== "deposit"
        );
      });
      breakdown[propertyId].revenue += propertyPayments.reduce(
        (sum: number, p: any) => sum + p.amount_cents / 100,
        0
      );
    });

    // Finalize occupancy/availability per property
    Object.entries(breakdown).forEach(([propertyId, data]) => {
      const occupiedRooms =
        occupiedRoomIdsByProperty.get(propertyId)?.size || 0;
      data.occupiedRooms = occupiedRooms;
      data.occupancyRate =
        data.totalRooms > 0 ? (occupiedRooms / data.totalRooms) * 100 : 0;
      data.tenantNames = Array.from(new Set(data.tenantNames)).slice(0, 50);
    });

    return Object.values(breakdown);
  }, [
    propertyScopedBookings,
    filteredPayments,
    propertyScopedRooms,
    profiles,
    propertyScopedPayments,
  ]);

  const propertyPerformanceTotalPages = Math.max(
    1,
    Math.ceil(propertyBreakdown.length / PROPERTY_PERFORMANCE_PAGE_SIZE)
  );

  const paginatedPropertyBreakdown = useMemo(() => {
    const start =
      (propertyPerformancePage - 1) * PROPERTY_PERFORMANCE_PAGE_SIZE;
    return propertyBreakdown.slice(
      start,
      start + PROPERTY_PERFORMANCE_PAGE_SIZE
    );
  }, [propertyBreakdown, propertyPerformancePage]);

  useEffect(() => {
    setPropertyPerformancePage((p) =>
      Math.min(p, propertyPerformanceTotalPages)
    );
  }, [propertyPerformanceTotalPages]);

  useEffect(() => {
    setPropertyPerformancePage(1);
  }, [propertyBreakdown.length]);

  const revenueTrend = useMemo(() => {
    const startOfDay = (d: Date) => {
      const x = new Date(d);
      x.setHours(0, 0, 0, 0);
      return x;
    };

    const daysBetween = (a: Date, b: Date) => {
      const ms = startOfDay(b).getTime() - startOfDay(a).getTime();
      return Math.max(0, Math.floor(ms / (1000 * 60 * 60 * 24)));
    };

    type Bucket = { key: string; label: string; start: Date; end: Date };
    type Granularity = "hour" | "day" | "month";

    const rangeDays = daysBetween(dateRange.start, dateRange.end) + 1;
    let granularity: Granularity = "day";
    if (timeRange === "today") granularity = "hour";
    else if (timeRange === "year") granularity = "month";
    else if (timeRange === "custom") {
      if (rangeDays <= 2) granularity = "hour";
      else if (rangeDays <= 45) granularity = "day";
      else granularity = "month";
    }

    const buckets: Bucket[] = [];
    const makeEndExclusive = (d: Date) => new Date(d.getTime());

    if (granularity === "hour") {
      const base = startOfDay(dateRange.start);
      const end = new Date(dateRange.end);
      const hoursTotal = Math.max(
        1,
        Math.ceil((end.getTime() - base.getTime()) / (1000 * 60 * 60))
      );
      const maxHours = Math.min(hoursTotal, 48);
      for (let i = 0; i < maxHours; i++) {
        const s = new Date(base.getTime() + i * 60 * 60 * 1000);
        const e = new Date(s.getTime() + 60 * 60 * 1000);
        const label = s.toLocaleTimeString(
          language === "de" ? "de-DE" : "en-GB",
          {
            hour: "2-digit",
            minute: "2-digit",
          }
        );
        buckets.push({ key: s.toISOString(), label, start: s, end: e });
      }
    } else if (granularity === "day") {
      const base = startOfDay(dateRange.start);
      for (let i = 0; i < rangeDays; i++) {
        const s = new Date(base);
        s.setDate(s.getDate() + i);
        const e = new Date(s);
        e.setDate(e.getDate() + 1);
        const label = s.toLocaleDateString(
          language === "de" ? "de-DE" : "en-GB",
          {
            day: "2-digit",
            month: "short",
          }
        );
        buckets.push({
          key: s.toISOString().slice(0, 10),
          label,
          start: s,
          end: e,
        });
      }
    } else {
      const s0 = new Date(
        dateRange.start.getFullYear(),
        dateRange.start.getMonth(),
        1
      );
      const e0 = new Date(
        dateRange.end.getFullYear(),
        dateRange.end.getMonth(),
        1
      );
      const months =
        (e0.getFullYear() - s0.getFullYear()) * 12 +
        (e0.getMonth() - s0.getMonth()) +
        1;
      for (let i = 0; i < months; i++) {
        const s = new Date(s0.getFullYear(), s0.getMonth() + i, 1);
        const e = new Date(s.getFullYear(), s.getMonth() + 1, 1);
        const label = s.toLocaleDateString(
          language === "de" ? "de-DE" : "en-GB",
          {
            month: "short",
            year: "2-digit",
          }
        );
        buckets.push({
          key: `${s.getFullYear()}-${s.getMonth() + 1}`,
          label,
          start: s,
          end: e,
        });
      }
    }

    const data = buckets.map((b) => ({
      label: b.label,
      rent: 0,
      deposit: 0,
      total: 0,
      _start: b.start.getTime(),
      _end: makeEndExclusive(b.end).getTime(),
    }));

    const add = (row: any, amount: number, type: "rent" | "deposit") => {
      if (type === "rent") row.rent += amount;
      else row.deposit += amount;
      row.total += amount;
    };

    (filteredPayments || []).forEach((p: any) => {
      if (p.status !== "paid") return;
      const type =
        p.type === "deposit" ? "deposit" : p.type === "rent" ? "rent" : null;
      if (!type) return;

      const dt = getPaymentBucketDateForAnalytics(p);
      if (!dt || Number.isNaN(dt.getTime())) return;
      const ts = dt.getTime();
      const amount = (p.amount_cents || 0) / 100;
      const idx = data.findIndex((b) => ts >= b._start && ts < b._end);
      if (idx >= 0) add(data[idx], amount, type);
    });

    return { data };
  }, [filteredPayments, dateRange, timeRange, language]);

  const formatCurrency = (amount: number) => {
    return `€${amount.toLocaleString(language === "de" ? "de-DE" : "en-GB", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const formatDateTime = (iso: string | null | undefined) => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString(language === "de" ? "de-DE" : "en-GB", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  };

  const formatDate = (iso: string | null | undefined) => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString(language === "de" ? "de-DE" : "en-GB", {
      dateStyle: "medium",
    });
  };

  const propertyOptions = useMemo(() => {
    const byId = new Map<string, string>();
    (rooms || []).forEach((r: any) => {
      const p = r.properties;
      if (!p?.id) return;
      const id = String(p.id);
      if (!byId.has(id))
        byId.set(id, (p.title || p.city || "Property").toString());
    });
    return Array.from(byId.entries())
      .map(([id, label]) => ({ id, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [rooms]);

  const periodLabel = useMemo(() => {
    switch (timeRange) {
      case "today":
        return t("admin.analytics.periodToday", "Today");
      case "week":
        return t("admin.analytics.periodThisWeek", "This week");
      case "month":
        return t("admin.analytics.periodThisMonth", "This month");
      case "year":
        return t("admin.analytics.periodThisYear", "This year");
      case "custom":
        return customFrom && customTo
          ? `${customFrom} → ${customTo}`
          : t("admin.analytics.periodCustomRange", "Custom range");
      case "all":
        return t("admin.analytics.periodAllTime", "All time");
      default:
        return t("admin.analytics.periodSelectedDefault", "Selected period");
    }
  }, [timeRange, customFrom, customTo, t]);

  const propertyScopeLabel = useMemo(() => {
    if (selectedProperty === "all")
      return t("admin.analytics.scopeAllProperties", "All properties");
    const p = propertyOptions.find((x) => x.id === selectedProperty);
    return p
      ? `${t("admin.analytics.propertyPrefix", "Property")}: ${p.label}`
      : t("admin.analytics.scopeSelectedPropertyFallback", "Selected property");
  }, [selectedProperty, propertyOptions, t]);

  const activeTenantExportRows = useMemo((): ActiveTenantExportRow[] => {
    const fmtLease = (iso: string | null | undefined) => {
      if (!iso) return "—";
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return "—";
      return d.toLocaleDateString(language === "de" ? "de-DE" : "en-GB", {
        dateStyle: "medium",
      });
    };

    const now = new Date();
    const { paidRentUsers, paidDepositUsers } = buildPaidUserSets(
      testFilteredPayments as any[]
    );

    const eligibleBooking = (b: any) => {
      if (!isActiveTenantBooking(b, now, paidRentUsers, paidDepositUsers))
        return false;
      const pid =
        b?.rooms?.properties?.id != null ? String(b.rooms.properties.id) : "";
      if (selectedProperty !== "all" && pid !== selectedProperty) return false;
      return true;
    };

    const candidates = (visibleBookings || []).filter(eligibleBooking);
    const byUid = new Map<string, (typeof candidates)[0]>();
    for (const b of candidates) {
      const uid = String(b.user_id);
      const prev = byUid.get(uid);
      if (!prev) {
        byUid.set(uid, b);
        continue;
      }
      const t1 = new Date(b.start_date || 0).getTime();
      const t2 = new Date(prev.start_date || 0).getTime();
      if (t1 > t2) byUid.set(uid, b);
    }

    const profileById = new Map(
      (profiles || []).map((p: any) => [String(p.id), p])
    );

    const rows: ActiveTenantExportRow[] = [];
    for (const b of byUid.values()) {
      const uid = String(b.user_id);
      const prof = profileById.get(uid);
      rows.push({
        name: (prof?.name || "").trim() || "—",
        email: (prof?.email || "").trim() || "—",
        property: b?.rooms?.properties?.title || "—",
        room: b?.rooms?.title || "—",
        city: b?.rooms?.properties?.city || "—",
        leaseStart: b.start_date ? fmtLease(b.start_date) : "—",
        leaseEnd: b.end_date ? fmtLease(b.end_date) : "—",
      });
    }

    return rows.sort((a, b) => a.name.localeCompare(b.name));
  }, [
    visibleBookings,
    testFilteredPayments,
    profiles,
    selectedProperty,
    language,
  ]);

  const analyticsMetricsSnapshot = useMemo((): AnalyticsMetricsSnapshot => {
    return {
      paidRent: financialMetrics.paidRent,
      expectedRent: financialMetrics.expectedRent,
      rentCollectionRate: financialMetrics.rentCollectionRate,
      paidDeposit: financialMetrics.paidDeposit,
      expectedDeposit: financialMetrics.expectedDeposit,
      depositCollectionRate: financialMetrics.depositCollectionRate,
      overdueRentAmount: financialMetrics.overdueRentAmount,
      totalRooms: occupancyMetrics.totalRooms,
      occupiedRooms: occupancyMetrics.occupiedRooms,
      occupancyRate: occupancyMetrics.occupancyRate,
      activeTenantCount: occupancyMetrics.activeTenants,
    };
  }, [financialMetrics, occupancyMetrics]);

  const handleExportPdf = async () => {
    try {
      await downloadAnalyticsPdf({
        metrics: analyticsMetricsSnapshot,
        tenants: activeTenantExportRows,
        periodLabel,
        propertyScopeLabel,
      });
      toast.success(t("admin.analytics.toastPdfDownloaded", "PDF downloaded"));
    } catch {
      toast.error(
        t("admin.analytics.toastPdfFailed", "Could not generate PDF")
      );
    }
  };

  const handleExportExcel = () => {
    try {
      downloadAnalyticsExcel({
        metrics: analyticsMetricsSnapshot,
        tenants: activeTenantExportRows,
        periodLabel,
        propertyScopeLabel,
      });
      toast.success(
        t("admin.analytics.toastExcelDownloaded", "Excel file downloaded")
      );
    } catch {
      toast.error(
        t("admin.analytics.toastExcelFailed", "Could not export Excel")
      );
    }
  };

  return (
    <div className="space-y-6">
      {/* Header with Filters */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">
            {t("admin.analytics.pageTitle", "Analytics & Reporting")}
          </h2>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {/* Test/CoLiviq accounts toggle — same predicate as Bookings &
              Tenants, laid out as one inline control in the filter row. */}
          <div className="flex h-10 items-center gap-2.5 rounded-xl border bg-background px-3 whitespace-nowrap">
            <Switch
              id="analytics-include-test-accounts"
              checked={includeTestAccounts}
              onCheckedChange={setIncludeTestAccounts}
            />
            <Label
              htmlFor="analytics-include-test-accounts"
              className="text-sm text-muted-foreground cursor-pointer"
            >
              {brandText(
                t(
                  "admin.tenants.includeTestAccounts",
                  "Include test/coliviq accounts"
                )
              )}
            </Label>
          </div>

          <Select
            value={selectedProperty}
            onValueChange={(val) => setSelectedProperty(val)}
          >
            <SelectTrigger className="w-[220px]">
              <SelectValue
                placeholder={t(
                  "admin.analytics.filterAllProperties",
                  "All properties"
                )}
              />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">
                {t("admin.analytics.filterAllProperties", "All properties")}
              </SelectItem>
              {propertyOptions.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={timeRange}
            onValueChange={(val: TimeRange) => setTimeRange(val)}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="today">
                {t("admin.analytics.rangeToday", "Today")}
              </SelectItem>
              <SelectItem value="week">
                {t("admin.analytics.rangeThisWeek", "This Week")}
              </SelectItem>
              <SelectItem value="month">
                {t("admin.analytics.rangeThisMonth", "This Month")}
              </SelectItem>
              <SelectItem value="year">
                {t("admin.analytics.rangeThisYear", "This Year")}
              </SelectItem>
              <SelectItem value="custom">
                {t("admin.analytics.rangeCustom", "Custom range")}
              </SelectItem>
              <SelectItem value="all">
                {t("admin.analytics.rangeAllTime", "All Time")}
              </SelectItem>
            </SelectContent>
          </Select>

          {timeRange === "custom" && (
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-2 rounded-xl border bg-background px-3 py-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <input
                  type="date"
                  value={customFrom}
                  onChange={(e) => setCustomFrom(e.target.value)}
                  className="bg-transparent text-sm outline-none"
                />
              </div>
              <span className="text-sm text-muted-foreground">to</span>
              <div className="flex items-center gap-2 rounded-xl border bg-background px-3 py-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <input
                  type="date"
                  value={customTo}
                  onChange={(e) => setCustomTo(e.target.value)}
                  className="bg-transparent text-sm outline-none"
                />
              </div>
            </div>
          )}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="gap-2">
                <Download className="w-4 h-4" />
                {t("admin.analytics.actionExport", "Export")}
                <ChevronDown className="w-4 h-4 opacity-60" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              <DropdownMenuItem
                className="cursor-pointer"
                onClick={() => void handleExportPdf()}
              >
                PDF
              </DropdownMenuItem>
              <DropdownMenuItem
                className="cursor-pointer"
                onClick={handleExportExcel}
              >
                {t("admin.analytics.actionExcel", "Excel")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Total CoLiviq revenue — hero */}
      {!ownershipDataReady ? (
        <Card className="rounded-3xl border bg-card shadow-sm overflow-hidden">
          <CardContent className="p-6 sm:p-8">
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              {t("admin.analytics.totalRevenue", "Total coliviq revenue")}
            </p>
            <p
              className={`mt-3 text-sm ${ownershipDataError ? "text-rose-700" : "text-muted-foreground"}`}
            >
              {ownershipDataError
                ? brandText(
                    t(
                      "admin.analytics.ownershipDataErrorHint",
                      "Couldn't load the ownership data this figure depends on. Refresh the page — showing a number here would misclassify third-party rent as coliviq's own."
                    )
                  )
                : t(
                    "admin.analytics.ownershipDataLoadingHint",
                    "Loading ownership data…"
                  )}
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card className="rounded-3xl border bg-card shadow-sm overflow-hidden">
          <CardContent className="p-6 sm:p-8">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                  {brandText(
                    t("admin.analytics.totalRevenue", "Total coliviq revenue")
                  )}
                  {" · "}
                  {t("admin.analytics.netSuffix", "Net")}
                </p>
                <div className="mt-2 text-4xl sm:text-5xl font-bold tracking-tight">
                  {formatCurrency(revenueStreams.total)}
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  <span className="inline-flex items-center rounded-md border border-emerald-100 bg-emerald-50 px-1.5 py-0.5 text-xs font-semibold text-emerald-700">
                    {heroPeriod.name}
                  </span>
                </p>
              </div>

              {/* Period-over-period comparison (dynamic) */}
              {revenueComparison && (
                <div className="flex flex-col items-end gap-1.5 text-right">
                  <span
                    className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-sm font-semibold ${
                      revenueComparison.direction === "up"
                        ? "bg-emerald-50 text-emerald-700 border border-emerald-100"
                        : revenueComparison.direction === "down"
                          ? "bg-rose-50 text-rose-700 border border-rose-100"
                          : "bg-muted text-muted-foreground border"
                    }`}
                  >
                    {revenueComparison.direction === "up" ? (
                      <ArrowUpRight className="h-4 w-4" />
                    ) : revenueComparison.direction === "down" ? (
                      <ArrowDownRight className="h-4 w-4" />
                    ) : null}
                    {revenueComparison.pct != null
                      ? `${revenueComparison.pct >= 0 ? "+" : ""}${revenueComparison.pct.toFixed(1)}%`
                      : revenueComparison.current > 0
                        ? "New"
                        : "—"}
                    <span className="font-normal opacity-80">
                      vs {revenueComparison.label}
                    </span>
                  </span>
                  <span className="text-xs text-muted-foreground tabular-nums">
                    {revenueComparison.label}:{" "}
                    {formatCurrency(revenueComparison.prevTotal)}
                    {revenueComparison.delta !== 0 && (
                      <>
                        {" · "}
                        <span
                          className={
                            revenueComparison.direction === "up"
                              ? "text-emerald-600"
                              : revenueComparison.direction === "down"
                                ? "text-rose-600"
                                : ""
                          }
                        >
                          {revenueComparison.delta >= 0 ? "+" : "−"}
                          {formatCurrency(Math.abs(revenueComparison.delta))}
                        </span>
                      </>
                    )}
                  </span>
                </div>
              )}
            </div>

            {/* Stacked proportion bar */}
            <div className="mt-5 flex h-3 w-full overflow-hidden rounded-full bg-muted">
              <div
                className="h-full bg-emerald-400 transition-all"
                style={{ width: `${revenueStreams.rentPct}%` }}
                title={`Rent · ${revenueStreams.rentPct.toFixed(1)}%`}
              />
              <div
                className="h-full bg-teal-800 transition-all"
                style={{ width: `${revenueStreams.managementPct}%` }}
                title={`Management · ${revenueStreams.managementPct.toFixed(1)}%`}
              />
              <div
                className="h-full bg-orange-300 transition-all"
                style={{ width: `${revenueStreams.saasPct}%` }}
                title={`SaaS · ${revenueStreams.saasPct.toFixed(1)}%`}
              />
            </div>

            {/* Legend: colour · stream · share · amount */}
            <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2">
              {revenueLegend.map((s) => (
                <div key={s.key} className="flex items-center gap-2">
                  <span className={`h-2.5 w-2.5 rounded-full ${s.dot}`} />
                  <span className="text-sm font-medium">{s.label}</span>
                  <span className="text-sm text-muted-foreground tabular-nums">
                    {s.pct.toFixed(1)}%
                  </span>
                  <span className="text-sm text-muted-foreground tabular-nums">
                    · {formatCurrency(s.amount)}
                  </span>
                </div>
              ))}
            </div>

            {/* Gross inflow vs. what is passed on to third-party owners */}
            <div className="mt-5 flex flex-wrap items-center justify-between gap-x-6 gap-y-2 border-t border-dashed pt-4 text-sm">
              <div className="flex flex-wrap items-baseline gap-x-2">
                <span className="text-muted-foreground">
                  {t("admin.analytics.grossRevenue", "Gross revenue")}
                </span>
                <span className="font-semibold tabular-nums">
                  {formatCurrency(revenueStreams.gross)}
                </span>
              </div>
              <div className="flex flex-wrap items-baseline gap-x-2">
                <span className="font-semibold tabular-nums">
                  {formatCurrency(revenueStreams.split.passedOn)}
                </span>
                <span className="text-muted-foreground">
                  {t(
                    "admin.analytics.passedOnToOwners",
                    "collected and passed on to owners"
                  )}
                </span>
              </div>
            </div>
            {platformLessorFetched &&
              !platformLessor &&
              revenueStreams.split.passThroughGross > 0 && (
                <p className="mt-2 text-xs text-muted-foreground">
                  {brandText(
                    t(
                      "admin.analytics.noPlatformLessorHint",
                      "No owner is flagged as the coliviq platform entity, so every owner-linked unit counts as third-party here. Flag coliviq's own owner record under Owners to see its rent as coliviq revenue."
                    )
                  )}
                </p>
              )}

            {/* Revenue stream cards */}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
              <button
                type="button"
                onClick={() => navigate("/admin?tab=tenants")}
                className="text-left rounded-2xl border bg-background p-4 transition hover:shadow-md hover:ring-2 hover:ring-emerald-200 focus:outline-none focus:ring-2 focus:ring-emerald-300"
              >
                <div className="flex items-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
                  <span className="text-sm font-medium">
                    {t("admin.analytics.rentRevenue", "Rent revenue")}
                  </span>
                </div>
                <div className="mt-2 text-2xl font-bold">
                  {formatCurrency(revenueStreams.rent)}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {brandText(
                    t(
                      "admin.analytics.rentCardSubtitle",
                      "Direct rent collected · coliviq-owned units"
                    )
                  )}{" "}
                  →
                </p>
                <Sparkline
                  values={streamSparklines.rent}
                  className="bg-emerald-400"
                  label={t("admin.analytics.rentRevenue", "Rent revenue")}
                />
                <p className="mt-3 border-t pt-2.5 text-xs text-muted-foreground tabular-nums">
                  {t(
                    "admin.analytics.rentCardExcludes",
                    "Excludes rent collected for third-party owners"
                  )}{" "}
                  · {formatCurrency(revenueStreams.split.passThroughGross)}
                </p>
              </button>

              <button
                type="button"
                onClick={() => navigate("/admin/owners")}
                className="text-left rounded-2xl border bg-background p-4 transition hover:shadow-md hover:ring-2 hover:ring-teal-200 focus:outline-none focus:ring-2 focus:ring-teal-300"
              >
                <div className="flex items-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-full bg-teal-800" />
                  <span className="text-sm font-medium">
                    {t("admin.analytics.mgmtRevenue", "Management revenue")}
                  </span>
                </div>
                <div className="mt-2 text-2xl font-bold">
                  {formatCurrency(revenueStreams.management)}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {t(
                    "admin.analytics.mgmtCardSubtitle",
                    "Net management fees from owners"
                  )}{" "}
                  →
                </p>
                <Sparkline
                  values={streamSparklines.management}
                  className="bg-teal-800"
                  label={t("admin.analytics.mgmtRevenue", "Management revenue")}
                />
                <p className="mt-3 border-t pt-2.5 text-xs text-muted-foreground tabular-nums">
                  {t(
                    "admin.analytics.mgmtFeeInvoicesPaid",
                    "Fee invoices paid"
                  )}{" "}
                  {formatCurrency(revenueStreams.managementInvoiced)}
                  {" · "}
                  {formatCurrency(revenueStreams.split.retainedFees)}{" "}
                  {/* Says WHY a fee sits in this half rather than the
                    paid-invoice half: it belongs to a unit that issues no
                    invoice at all. Without the qualifier the two figures
                    read as rival measures of the same thing. */}
                  {t(
                    "admin.analytics.mgmtNettedFromRent",
                    "netted from rent where no invoice is issued"
                  )}
                  {managementMetrics.overdueCount > 0 && (
                    <>
                      {" · "}
                      <span className="text-rose-600">
                        {managementMetrics.overdueCount}{" "}
                        {t("admin.analytics.mgmtOverdueSuffix", "overdue")}
                      </span>
                    </>
                  )}
                </p>
              </button>

              <div className="rounded-2xl border bg-background p-4">
                <div className="flex items-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-full bg-orange-300" />
                  <span className="text-sm font-medium">
                    {t("admin.analytics.saasRevenue", "SaaS revenue")}
                  </span>
                  <Badge variant="secondary" className="ml-auto text-[10px]">
                    MRR
                  </Badge>
                </div>
                <div className="mt-2 text-2xl font-bold">
                  {formatCurrency(revenueStreams.saas)}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {t(
                    "admin.analytics.saasCardSubtitle",
                    "Active subscriptions"
                  )}
                </p>
                <Sparkline
                  values={streamSparklines.saas}
                  className="bg-orange-300"
                  label={t("admin.analytics.saasRevenue", "SaaS revenue")}
                />
                <p className="mt-3 border-t pt-2.5 text-xs text-muted-foreground tabular-nums">
                  {revenueStreams.saasInvoiceCount > 0
                    ? `${revenueStreams.saasInvoiceCount} ${t(
                        "admin.analytics.saasInvoicesPaid",
                        "paid SaaS invoice(s) this period"
                      )}`
                    : t(
                        "admin.analytics.saasNoRevenue",
                        "No SaaS revenue this period"
                      )}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Key KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {/* Rent collection */}
        <Card className="rounded-2xl border bg-card shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">
              {t("admin.analytics.rentCollectionRate", "Rent collection rate")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {financialMetrics.rentCollectionRate.toFixed(1)}%
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {formatCurrency(financialMetrics.paidRent)} of{" "}
              {formatCurrency(financialMetrics.expectedRent)} collected
            </p>
            <MiniBar
              pct={financialMetrics.rentCollectionRate}
              className="bg-sky-500"
            />
          </CardContent>
        </Card>

        {/* Overdue rent */}
        <Card className="rounded-2xl border bg-card shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">
              {t("admin.analytics.overdueRent", "Overdue rent")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-destructive">
              {formatCurrency(financialMetrics.overdueRentAmount)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {overdueTenantsCount > 0
                ? `${overdueTenantsCount} ${
                    overdueTenantsCount === 1
                      ? t(
                          "admin.analytics.overdueTenantOne",
                          "tenant requires attention"
                        )
                      : t(
                          "admin.analytics.overdueTenantMany",
                          "tenants require attention"
                        )
                  }`
                : t("admin.analytics.allRentCollected", "All rent collected")}
            </p>
            <p className="text-xs text-muted-foreground mt-2">
              {financialMetrics.expectedRent > 0
                ? `${(100 - financialMetrics.rentCollectionRate).toFixed(1)}% ${t(
                    "admin.analytics.rentStillUncollected",
                    "of rent still uncollected"
                  )}`
                : t("admin.analytics.noRentDue", "No rent due this period")}
            </p>
          </CardContent>
        </Card>

        {/* Outstanding management fees */}
        <Card className="rounded-2xl border bg-card shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">
              {t(
                "admin.analytics.outstandingFees",
                "Outstanding management fees"
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(managementMetrics.outstanding)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {managementMetrics.outstandingCount > 0
                ? `${managementMetrics.outstandingCount} invoice${
                    managementMetrics.outstandingCount !== 1 ? "s" : ""
                  } from ${managementMetrics.ownerCount} owner${
                    managementMetrics.ownerCount !== 1 ? "s" : ""
                  }`
                : t(
                    "admin.analytics.noFeesOutstanding",
                    "No fees outstanding — all owners settled"
                  )}
            </p>

            {/* Per-owner breakdown (fees are billed to the property owner) */}
            {managementMetrics.owners.length > 0 && (
              <div className="mt-3 space-y-2">
                {managementMetrics.owners.slice(0, 3).map((o) => (
                  <div
                    key={o.name}
                    className="flex items-center gap-2.5 rounded-xl border bg-muted/30 px-2.5 py-1.5"
                  >
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-amber-100 text-[10px] font-semibold text-amber-800">
                      {initialsFromName(o.name)}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-xs font-medium">
                      {o.name}
                    </span>
                    {o.overdue > 0 && (
                      <span className="shrink-0 rounded-full bg-rose-50 px-1.5 py-0.5 text-[10px] font-medium text-rose-700">
                        {o.overdue} overdue
                      </span>
                    )}
                    <span className="shrink-0 text-xs font-semibold tabular-nums">
                      {formatCurrency(o.amount)}
                    </span>
                  </div>
                ))}
                {managementMetrics.owners.length > 3 && (
                  <button
                    type="button"
                    onClick={() => navigate("/admin/owners")}
                    className="w-full rounded-xl border border-dashed px-2.5 py-1.5 text-left text-xs font-medium text-muted-foreground transition hover:bg-muted/50 hover:text-foreground"
                  >
                    +{managementMetrics.owners.length - 3} more owner
                    {managementMetrics.owners.length - 3 !== 1 ? "s" : ""} →
                  </button>
                )}
              </div>
            )}

            <p className="mt-3 text-xs text-muted-foreground">
              {managementMetrics.invoiced > 0
                ? `${formatCurrency(
                    // Invoiced fees only — the fee netted from pass-through
                    // rent was never invoiced, so it can't count as collected
                    // against `invoiced` (it would read >100%).
                    revenueStreams.managementInvoiced
                  )} of ${formatCurrency(
                    managementMetrics.invoiced
                  )} collected${
                    managementMetrics.overdueCount > 0
                      ? ` · ${managementMetrics.overdueCount} overdue`
                      : ""
                  }`
                : t(
                    "admin.analytics.noFeesInvoiced",
                    "No management fees invoiced"
                  )}
            </p>
          </CardContent>
        </Card>

        {/* Deposit revenue */}
        <Card className="rounded-2xl border bg-card shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">
              {t("admin.analytics.depositRevenue", "Deposit revenue")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(financialMetrics.paidDeposit)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {financialMetrics.expectedDeposit > 0
                ? `${financialMetrics.depositCollectionRate.toFixed(
                    1
                  )}% of ${formatCurrency(financialMetrics.expectedDeposit)}`
                : `Security deposits · ${depositPeriodLabel}`}
            </p>
            <MiniBar
              pct={
                financialMetrics.expectedDeposit > 0
                  ? financialMetrics.depositCollectionRate
                  : 0
              }
              className="bg-indigo-500"
            />
          </CardContent>
        </Card>

        {/* Occupancy */}
        <Card className="rounded-2xl border bg-card shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">
              {t("admin.analytics.occupancyRate", "Occupancy rate")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {occupancyMetrics.occupancyRate.toFixed(1)}%
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {occupancyMetrics.occupiedRooms} of {occupancyMetrics.totalRooms}{" "}
              rooms occupied · {occupancyMetrics.availableRooms} available
            </p>
            <MiniBar
              pct={occupancyMetrics.occupancyRate}
              className="bg-slate-700"
            />
          </CardContent>
        </Card>

        {/* Total active tenants */}
        <Card className="rounded-2xl border bg-card shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">
              {t("admin.analytics.totalActiveTenants", "Total active tenants")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {occupancyMetrics.activeTenants}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Signed contract · rent &amp; deposit paid
            </p>
            <p className="text-xs text-muted-foreground mt-2">
              {occupancyMetrics.occupiedRooms} rooms occupied
              {selectedProperty === "all" ? " across the portfolio" : ""}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* City Overview (Tutzing / Augsburg) */}
      <Card>
        <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <CardTitle>
              {t("admin.analytics.cityOverviewTitle", "City Overview")}
            </CardTitle>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="relative w-full sm:w-[260px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={roomSearch}
                onChange={(e) => setRoomSearch(e.target.value)}
                placeholder={t(
                  "admin.analytics.searchRooms",
                  "Search rooms..."
                )}
                className="pl-9"
              />
            </div>
            <Select value={cityFilter} onValueChange={setCityFilter}>
              <SelectTrigger className="w-full sm:w-[200px]">
                <SelectValue
                  placeholder={t(
                    "admin.analytics.filterAllCities",
                    "All cities"
                  )}
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  {t("admin.analytics.filterAllCities", "All cities")}
                </SelectItem>
                {cityOptions.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {visibleCityBreakdown.map((c) => {
              const rentRate =
                c.expectedRent > 0 ? (c.paidRent / c.expectedRent) * 100 : 0;
              const depositRate =
                c.expectedDeposit > 0
                  ? (c.paidDeposit / c.expectedDeposit) * 100
                  : 0;
              return (
                <div key={c.city} className="border rounded-xl p-4 bg-card">
                  <h4 className="font-semibold">{c.city}</h4>

                  <div className="mt-4 space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">
                        {t("admin.analytics.rent", "Rent")}
                      </span>
                      <span className="font-medium">
                        {formatCurrency(c.paidRent)} /{" "}
                        {formatCurrency(c.expectedRent)} ({rentRate.toFixed(0)}
                        %)
                      </span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-2">
                      <div
                        className="bg-primary h-2 rounded-full"
                        style={{ width: `${Math.min(rentRate, 100)}%` }}
                      />
                    </div>

                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">
                        {t("admin.analytics.deposit", "Deposit")}
                      </span>
                      <span className="font-medium">
                        {formatCurrency(c.paidDeposit)} /{" "}
                        {formatCurrency(c.expectedDeposit)} (
                        {depositRate.toFixed(0)}%)
                      </span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-2">
                      <div
                        className="bg-slate-900 h-2 rounded-full"
                        style={{ width: `${Math.min(depositRate, 100)}%` }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}

            {visibleCityBreakdown.length === 0 && (
              <p className="text-center text-muted-foreground py-8">
                {t("admin.analytics.cityEmpty", "No city data available")}
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Revenue Trend Chart */}
      <Card>
        <CardHeader>
          <CardTitle>
            {t("admin.analytics.revenueTrendTitle", "Revenue Trend")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[280px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={revenueTrend.data}
                margin={{ top: 8, right: 16, left: 8, bottom: 8 }}
              >
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 12 }}
                  interval="preserveStartEnd"
                />
                <YAxis
                  tick={{ fontSize: 12 }}
                  tickFormatter={(v) =>
                    `€${Number(v).toLocaleString(language === "de" ? "de-DE" : "en-GB")}`
                  }
                />
                <Tooltip
                  formatter={(value: any, name: any) => {
                    const label =
                      name === "rent"
                        ? t("admin.analytics.rentLabel", "Rent")
                        : name === "deposit"
                          ? t("admin.analytics.depositLabel", "Deposit")
                          : name;
                    return [formatCurrency(Number(value)), label];
                  }}
                  labelFormatter={(label) =>
                    `${t("admin.analytics.periodPrefix", "Period")}: ${label}`
                  }
                  contentStyle={{ borderRadius: 12 }}
                />
                <Bar
                  dataKey="rent"
                  stackId="rev"
                  fill="hsl(var(--primary))"
                  radius={[6, 6, 0, 0]}
                />
                <Bar
                  dataKey="deposit"
                  stackId="rev"
                  fill="#0f172a"
                  radius={[6, 6, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-3 flex items-center justify-between text-sm text-muted-foreground">
            <span>
              Total:{" "}
              {formatCurrency(
                revenueTrend.data.reduce((s, r) => s + (r.total || 0), 0)
              )}
            </span>
            <span className="flex items-center gap-4">
              <span className="flex items-center gap-2">
                <span
                  className="h-2.5 w-2.5 rounded-sm"
                  style={{ background: "hsl(var(--primary))" }}
                />
                Rent
              </span>
              <span className="flex items-center gap-2">
                <span className="h-2.5 w-2.5 rounded-sm bg-slate-900" />
                Deposit
              </span>
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Property Performance */}
      <Card>
        <CardHeader>
          <CardTitle>
            {t("admin.analytics.propertyPerfTitle", "Property Performance")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {paginatedPropertyBreakdown.map((property) => {
              const collectionRate =
                property.expected > 0
                  ? (property.revenue / property.expected) * 100
                  : 0;
              const shownNames = property.tenantNames.slice(0, 3);
              const remainingNames = Math.max(
                0,
                property.tenants - shownNames.length
              );

              return (
                <div
                  key={property.id}
                  className="border rounded-2xl p-5 bg-card shadow-sm"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <h4 className="font-semibold truncate">
                        {property.property}
                      </h4>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-lg font-semibold">
                        {formatCurrency(property.revenue)}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {collectionRate.toFixed(0)}% collected
                      </p>
                    </div>
                  </div>

                  {/* Progress */}
                  <div className="mt-4">
                    <div className="flex items-center justify-between text-xs text-muted-foreground mb-2">
                      <span>Expected: {formatCurrency(property.expected)}</span>
                      <span>
                        Occupancy: {property.occupiedRooms}/
                        {property.totalRooms} (
                        {property.occupancyRate.toFixed(0)}%)
                      </span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-2.5">
                      <div
                        className="bg-primary h-2.5 rounded-full transition-all"
                        style={{ width: `${Math.min(collectionRate, 100)}%` }}
                      />
                    </div>
                  </div>

                  {/* Tenant names */}
                  <div className="mt-4">
                    <p className="text-xs font-medium text-muted-foreground mb-2">
                      {t("admin.analytics.occupiedBy", "Occupied by")}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {shownNames.map((name) => (
                        <span
                          key={name}
                          className="px-2.5 py-1 rounded-full border bg-muted/30 text-sm text-foreground"
                        >
                          {name}
                        </span>
                      ))}
                      {property.tenants > 0 && shownNames.length === 0 && (
                        <span className="text-sm text-muted-foreground">
                          {t(
                            "admin.analytics.tenantsAvailable",
                            "Tenants available"
                          )}
                        </span>
                      )}
                      {remainingNames > 0 && (
                        <span className="px-2.5 py-1 rounded-full border bg-background text-sm text-muted-foreground">
                          +{remainingNames} more
                        </span>
                      )}
                      {property.tenants === 0 && (
                        <span className="text-sm text-muted-foreground">
                          {t(
                            "admin.analytics.noActiveTenants",
                            "No active tenants"
                          )}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            {propertyBreakdown.length === 0 && (
              <p className="text-center text-muted-foreground py-8">
                {t(
                  "admin.analytics.propertyEmpty",
                  "No property data available"
                )}
              </p>
            )}
          </div>
          {propertyBreakdown.length > PROPERTY_PERFORMANCE_PAGE_SIZE && (
            <div className="flex items-center justify-between gap-4 pt-4 mt-4 border-t text-sm">
              <span className="text-muted-foreground">
                Showing{" "}
                {(propertyPerformancePage - 1) *
                  PROPERTY_PERFORMANCE_PAGE_SIZE +
                  1}
                –
                {Math.min(
                  propertyPerformancePage * PROPERTY_PERFORMANCE_PAGE_SIZE,
                  propertyBreakdown.length
                )}{" "}
                of {propertyBreakdown.length}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1"
                  onClick={() =>
                    setPropertyPerformancePage((p) => Math.max(1, p - 1))
                  }
                  disabled={propertyPerformancePage <= 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                  {t("admin.analytics.pagePrevious", "Previous")}
                </Button>
                <span className="text-muted-foreground px-2 tabular-nums">
                  Page {propertyPerformancePage} of{" "}
                  {propertyPerformanceTotalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1"
                  onClick={() =>
                    setPropertyPerformancePage((p) =>
                      Math.min(propertyPerformanceTotalPages, p + 1)
                    )
                  }
                  disabled={
                    propertyPerformancePage >= propertyPerformanceTotalPages
                  }
                >
                  {t("admin.analytics.pageNext", "Next")}
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
